import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import * as tus from 'tus-js-client';
import { REQUEST_FILE_UPLOAD } from '../../graphql/fileUploadMutation';
import { convertToHumanReadableSize } from '../../../utils/fileSize';

export const SUPPORTED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
export const FILE_MAX_AMOUNT = 5;
export const FILE_MAX_SIZE_MB = 10;
export const TOTAL_FILES_SIZE_MB = 10; // TODO: Change to 50 as soon as infrastructure allows.

const useFileUpload = ({ onFileRemoved, onFileUploaded }) => {
  const intl = useIntl();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [requestFileUpload] = useMutation(REQUEST_FILE_UPLOAD);
  const [fileMetaById, setFileMetaById] = useState({});

  const updateFileMeta = (metaId, newFileMeta) => {
    setFileMetaById((prevMap) => ({
      ...prevMap,
      [metaId]: {
        ...(prevMap[metaId] ?? {}),
        ...newFileMeta,
      },
    }));
  };

  const deleteFileMeta = (metaId) => {
    setFileMetaById((prevMap) => {
      const { [metaId]: deletedFile, ...otherFiles } = prevMap;
      return { ...otherFiles };
    });
  };

  const setTranslatedError = (id, values) => {
    setError(intl.formatMessage({ id }, values));
  };

  const checkIfFilesAreValid = (fileArray) => {
    setError(null);

    return fileArray.every((file) => {
      if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
        setTranslatedError('CONTACT_FORM_FILE_UPLOAD_ERR_UNSUPPORTED_FORMAT', {
          fileName: file.name,
          strong: (content) => <strong>{content}</strong>,
        });
        return false;
      }

      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > FILE_MAX_SIZE_MB) {
        setTranslatedError('CONTACT_FORM_FILE_UPLOAD_ERR_FILE_TOO_LARGE', {
          fileName: file.name,
          maxFileSize: FILE_MAX_SIZE_MB,
          strong: (content) => <strong>{content}</strong>,
        });
        return false;
      }
      return true;
    });
  };

  const removeFile = (metaId) => {
    setFiles((currentFiles) => {
      const otherFiles = currentFiles.filter((file) => file.metaId !== metaId);
      checkIfFilesAreValid(otherFiles);
      return otherFiles;
    });
    updateFileMeta(metaId, {
      wasRemoved: true,
    });
    const { uploadId } = fileMetaById[metaId] ?? {};
    if (uploadId) {
      onFileRemoved(uploadId);
    }
  };

  const addFiles = (fileList) => {
    // Augment files with additional data (and convert to array structure)
    let newFiles = Array.from(fileList).map((file) => {
      const fileWithId = file;
      fileWithId.metaId = `${file.name},${file.lastModified},${file.size}`;
      fileWithId.humanReadableSize = convertToHumanReadableSize(file.size);
      return fileWithId;
    });

    if (files.length + newFiles.length > FILE_MAX_AMOUNT) {
      setTranslatedError('CONTACT_FORM_FILE_UPLOAD_ERR_TOO_MANY_FILES', {
        maxAmount: FILE_MAX_AMOUNT,
      });
      return;
    }

    const filesAreValid = checkIfFilesAreValid(newFiles);
    if (!filesAreValid) {
      return;
    }

    const skippedFilesByMetaId = [];

    newFiles.forEach(async (file) => {
      const { metaId } = file;
      const abort = () => {
        removeFile(file.metaId);
        deleteFileMeta(file.metaId);
        setTranslatedError('CONTACT_FORM_FILE_UPLOAD_UNAVAILABLE');
      };

      const updateCurrentFileMeta = (newFileMeta) => updateFileMeta(file.metaId, newFileMeta);

      const { isUploaded, isUploading, wasRemoved } = fileMetaById[metaId] || {};
      if (isUploading || isUploaded) {
        if (wasRemoved && isUploaded) {
          updateCurrentFileMeta({ wasRemoved: false });
          // we don't want to skip adding it, but we don't need to upload again.
          return;
        }
        // this might happen if the user selects the same file twice.
        skippedFilesByMetaId.push(file.metaId);
        return;
      }
      updateCurrentFileMeta({
        isUploaded: false,
        isUploading: true,
        progress: 0,
        uploadId: null,
        uploadUrl: null,
        authToken: null,
      });

      try {
        /*
         * Step 1: Request an upload at upload/upload service via Xing-One
         */
        const gqlResult = await requestFileUpload({
          variables: { fileSize: file.size, fileName: file.name, fileType: file.type },
        });

        const { uploadRequest } = gqlResult.data ?? {};
        const { error: gqlError, success } = uploadRequest ?? {};
        if (gqlError && !success) {
          abort();
          return;
        }
        const { authToken, url: uploadUrl, id: uploadId } = success;

        updateCurrentFileMeta({
          uploadUrl,
          uploadId,
          authToken,
        });

        /*
         * Step 2: Perform the actual file upload via tus protocol
         */
        const upload = new tus.Upload(file, {
          // we use "uploadUrl" instead of "endpoint" because the upload is
          // already requested via graphql.
          uploadUrl,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          metadata: {
            filename: file.name,
            filetype: file.type,
          },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          onError() {
            abort();
          },
          onProgress(bytesUploaded, bytesTotal) {
            updateCurrentFileMeta({
              progress: bytesUploaded / bytesTotal,
            });
          },
          onSuccess() {
            updateCurrentFileMeta({
              isUploading: false,
              isUploaded: true,
              progress: 1,
            });
            onFileUploaded(uploadId);
          },
        });

        upload.start();
      } catch (e) {
        abort();
      }
    });

    // If files were skipped during upload, we don't want to add them
    newFiles = newFiles.filter((f) => !skippedFilesByMetaId.includes(f.metaId));
    setFiles((addedFiles) => {
      // Here we check the total size of all files combined.
      // This check is only necessary when the allowed total size is lower than the individual file size limit
      // multiplied by the allowed number of files. Depending on the infrastructure email limit this might be the case.
      const newArray = [...addedFiles, ...newFiles];
      const totalSizeInBytes = newArray.reduce((sum, file) => sum + file.size, 0);
      const sizeInMB = totalSizeInBytes / (1024 * 1024);
      if (sizeInMB > TOTAL_FILES_SIZE_MB) {
        setTranslatedError('CONTACT_FORM_FILE_UPLOAD_TOTAL_SIZE_WARNING', {
          size: TOTAL_FILES_SIZE_MB,
          strong: (content) => <strong>{content}</strong>,
        });
      }
      return newArray;
    });
  };

  return {
    addFiles,
    removeFile,
    files,
    error,
    fileMetaById,
  };
};

export default useFileUpload;
